"use client";

import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import Image from "next/image";
import { SearchCategories, SearchMerchant } from '@/services/dataTypes';
import { keywordsAction, searchAction } from '@/app/actions';
import { getCategoryHref, getMerchantHref } from '@/constants/hooks';
import { FontAwesomeIcon } from '@/constants/icons';
import { faStore, faBoxOpen, faMagnifyingGlass, faArrowRight, faFire } from '@fortawesome/free-solid-svg-icons';

interface Props {
  companyId: string;
  mer_slug: string;
  slug_type: string;
  cat_slug: string;
  dropdownAlign?: "left" | "right";
}

const SearchBar = ({ companyId, mer_slug, slug_type, cat_slug, dropdownAlign = "right" }: Props) => {
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams);
  const [search, setSearch] = useState<string>(params.get('query') || "");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [tagsData, setTagsData] = useState<any>([]);
  const [categoriesData, setCategoriesData] = useState<SearchCategories[]>([]);
  const [merchantData, setMerchantData] = useState<SearchMerchant[]>([]);
  const [errMsg, setErrMessage] = useState({ show: false, err: false, msg: '' });
  const [loading, setLoading] = useState(false);

  const pathName = usePathname();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  const handleKeywords = async () => {
    const response = await keywordsAction(companyId);
    if (response?.data) setTagsData(response.data);
  };

  useEffect(() => { handleKeywords(); }, [companyId]);

  const handleSearch = async (searchTerm: string) => {
    try {
      if (searchTerm.trim().length >= 3) {
        setLoading(true);
        const response = await searchAction(searchTerm, companyId);
        if (response?.data?.merchants?.length > 0 || response?.data?.categories?.length > 0) {
          setCategoriesData(response.data?.categories || []);
          setMerchantData(response.data?.merchants || []);
          setIsDropdownVisible(!!(pathName !== '/search'));
          setErrMessage({ show: false, err: false, msg: '' });
        } else {
          setMerchantData([]);
          setCategoriesData([]);
          setErrMessage({ show: true, err: true, msg: 'No result found.' });
        }
      } else {
        setMerchantData([]);
        setCategoriesData([]);
        setErrMessage({ show: true, err: true, msg: 'Input should be at least 3 characters.' });
      }
    } catch {
      setErrMessage({ show: true, err: true, msg: 'Something went wrong.' });
    } finally {
      setLoading(false);
    }
  };

  /* ── Merchant card ─────────────────────────────────────────── */
  const renderMerchant = (merchant: SearchMerchant) => (
    <Link
      key={merchant.merchant_name}
      href={getMerchantHref<SearchMerchant>(merchant, mer_slug, slug_type)}
      className="flex flex-col items-center justify-center p-2.5 border border-[#E5E7EB] rounded-xl bg-white hover:bg-[#F8FAFC] hover:border-[#1E3A5F]/30 hover:shadow-md transition-all duration-200 w-[90px] h-[90px] group shrink-0"
    >
      <div className="h-[44px] w-full flex items-center justify-center mb-1.5">
        {merchant.merchant_logo ? (
          <Image
            src={merchant.merchant_logo}
            alt={merchant.merchant_name}
            width={64}
            height={36}
            style={{ objectFit: "contain" }}
            className="block"
          />
        ) : (
          <div className="w-9 h-9 rounded-lg bg-[#F8FAFC] flex items-center justify-center">
            <FontAwesomeIcon icon={faStore} className="text-[#1E3A5F] w-4 h-4" />
          </div>
        )}
      </div>
      <strong className="text-[10px] text-[#111827] group-hover:text-[#1E3A5F] line-clamp-2 leading-tight transition-colors text-center w-full px-1 font-semibold">
        {merchant.merchant_name}
      </strong>
    </Link>
  );

  /* ── Category pill ─────────────────────────────────────────── */
  const renderCategories = (category: SearchCategories) => (
    <Link
      key={category?.name}
      href={getCategoryHref<SearchCategories>(category, cat_slug, slug_type)}
      className="inline-flex no-underline group"
    >
      <div className="px-3 py-1.5 border border-[#E5E7EB] rounded-full bg-[#F8FAFC] group-hover:bg-[#F97316] group-hover:border-[#F97316] transition-all duration-200">
        <span className="text-[11px] font-semibold text-[#4B5563] group-hover:text-white transition-colors">
          {category?.name}
        </span>
      </div>
    </Link>
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (search.trim() === "") {
        setMerchantData([]);
        setCategoriesData([]);
        setErrMessage({ show: false, err: false, msg: '' });
      } else {
        handleSearch(search);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search, companyId]);

  useEffect(() => {
    const query = params.get('query') || "";
    if (query !== search && document.activeElement !== inputRef.current) {
      setSearch(query);
    }
  }, [searchParams]);

  useEffect(() => {
    if (pathName === '/search') setIsDropdownVisible(false);
  }, [pathName]);

  const submitSearchNow = () => {
    if (search.trim()) {
      router.push(`/search?query=${encodeURIComponent(search.trim())}`);
      setIsDropdownVisible(false);
      setIsFocused(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false);
        setIsFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => { document.removeEventListener("mousedown", handleClickOutside); };
  }, []);

  // Show the dropdown whenever the input is focused (always give feedback)
  const showDropdown = isDropdownVisible && (isFocused || search.trim().length > 0 || tagsData.length > 0);
  const hasResults = merchantData.length > 0 || categoriesData.length > 0;

  return (
    <div ref={searchWrapperRef} className="relative w-full">

      {/* ── Search Input ───────────────────────────────────────── */}
      <div
        className={`relative flex items-center rounded-full px-3.5 py-2 border transition-all duration-300 w-full ${isFocused
            ? 'bg-white border-[#1E3A5F] shadow-[0_0_0_3px_rgba(30,58,95,0.10)]'
            : 'bg-[#F8FAFC] border-[#E5E7EB] shadow-none hover:border-[#1E3A5F]/40'
          }`}
      >
        {/* Leading search icon */}
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className={`w-3.5 h-3.5 mr-2.5 shrink-0 transition-colors duration-200 ${isFocused ? 'text-[#F97316]' : 'text-[#4B5563]'
            }`}
        />

        <input
          type="text"
          placeholder="Search stores . . ."
          className="bg-transparent border-none outline-none text-[13px] w-full text-[#111827] placeholder-[#4B5563] font-medium z-10"
          ref={inputRef}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsDropdownVisible(true);
          }}
          onFocus={() => {
            setIsFocused(true);
            setIsDropdownVisible(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && search.trim()) submitSearchNow();
            if (e.key === 'Escape') {
              setIsDropdownVisible(false);
              setIsFocused(false);
              inputRef.current?.blur();
            }
          }}
          aria-label="Search"
        />

        {/* Clear button */}
        {search.trim().length > 0 && (
          <button
            className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-[#E5E7EB] hover:bg-[#1E3A5F] text-[#4B5563] hover:text-white transition-all duration-200 ml-1"
            onClick={() => {
              setSearch('');
              setMerchantData([]);
              setCategoriesData([]);
              setErrMessage({ show: false, err: false, msg: '' });
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
              <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
        )}

        {/* Submit button */}
        <button
          className={`shrink-0 ml-2 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-200 ${search.trim().length > 0
              ? 'bg-[#F97316] text-white hover:bg-[#EA580C] shadow-sm'
              : 'bg-transparent text-[#4B5563] cursor-default'
            }`}
          onClick={submitSearchNow}
          aria-label="Search submit"
          disabled={search.trim().length === 0}
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} className="w-3 h-3" />
        </button>
      </div>

      {/* ── Dropdown Panel ─────────────────────────────────────── */}
      {showDropdown && (
        <div
          className={`absolute top-[calc(100%+8px)] w-full min-w-[300px] md:min-w-[420px] ${dropdownAlign === "left" ? "left-0" : "right-0"
            }`}
          style={{ zIndex: 99999 }}
        >
          {/* Arrow pointer */}
          <div className="absolute -top-[6px] right-6 w-3 h-3 bg-white border-l border-t border-[#E5E7EB] rotate-45" />

          <div className="bg-white rounded-2xl shadow-[0_16px_48px_-8px_rgba(30,58,95,0.18),0_4px_16px_-4px_rgba(30,58,95,0.08)] border border-[#E5E7EB] overflow-hidden">

            {/* Panel header */}
            <div className="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-[#F8FAFC]">
              <span className="text-[11px] font-bold text-[#4B5563] uppercase tracking-wider">
                {search.trim().length > 0 ? 'Search Results' : 'Popular Searches'}
              </span>
              {search.trim().length >= 3 && hasResults && !loading && (
                <button
                  onClick={submitSearchNow}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#F97316] hover:text-[#EA580C] transition-colors"
                >
                  View all
                  <FontAwesomeIcon icon={faArrowRight} className="w-2.5 h-2.5" />
                </button>
              )}
            </div>

            {/* Scrollable body */}
            <div className="max-h-[62vh] overflow-y-auto overscroll-contain p-4 space-y-5
              [&::-webkit-scrollbar]:w-1.5
              [&::-webkit-scrollbar-track]:bg-transparent
              [&::-webkit-scrollbar-thumb]:bg-[#E5E7EB]
              [&::-webkit-scrollbar-thumb]:rounded-full
              [&::-webkit-scrollbar-thumb:hover]:bg-[#1E3A5F]/30">

              {/* Loading */}
              {loading && search.trim().length >= 3 && merchantData.length === 0 && categoriesData.length === 0 && !errMsg.show && (
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <div className="relative w-8 h-8">
                    <div className="absolute inset-0 rounded-full border-2 border-[#E5E7EB]" />
                    <div className="absolute inset-0 rounded-full border-2 border-[#F97316] border-t-transparent animate-spin" />
                  </div>
                  <p className="text-[12px] text-[#4B5563] font-medium">
                    Searching for <span className="text-[#1E3A5F] font-bold">&ldquo;{search}&rdquo;</span>&hellip;
                  </p>
                </div>
              )}

              {/* Error / no results */}
              {errMsg.show && search.trim().length > 0 && !loading && (
                <div className={`flex items-start gap-3 px-3 py-3 rounded-xl text-[12px] font-semibold ${errMsg.err ? 'bg-red-50 text-[#DC2626]' : 'bg-[#DCFCE7] text-[#15803D]'
                  }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0 mt-px">
                    {errMsg.err
                      ? <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                      : <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                    }
                  </svg>
                  {errMsg.msg}
                </div>
              )}

              {/* Stores */}
              {merchantData.length > 0 && !loading && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded-md bg-[#F8FAFC] flex items-center justify-center">
                      <FontAwesomeIcon icon={faStore} className="text-[#1E3A5F] w-2.5 h-2.5" />
                    </div>
                    <span className="text-[12px] font-black text-[#111827]">Stores</span>
                    <span className="text-[10px] font-bold text-[#4B5563] bg-[#F8FAFC] border border-[#E5E7EB] px-1.5 py-0.5 rounded-full">
                      {Math.min(merchantData.length, 8)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {merchantData.slice(0, 8).map(renderMerchant)}
                  </div>
                </div>
              )}

              {/* Divider */}
              {merchantData.length > 0 && categoriesData.length > 0 && !loading && (
                <div className="border-t border-[#F8FAFC]" />
              )}

              {/* Categories */}
              {categoriesData.length > 0 && !loading && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded-md bg-[#FFF7ED] flex items-center justify-center">
                      <FontAwesomeIcon icon={faBoxOpen} className="text-[#F97316] w-2.5 h-2.5" />
                    </div>
                    <span className="text-[12px] font-black text-[#111827]">Categories</span>
                    <span className="text-[10px] font-bold text-[#4B5563] bg-[#F8FAFC] border border-[#E5E7EB] px-1.5 py-0.5 rounded-full">
                      {Math.min(categoriesData.length, 10)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {categoriesData.slice(0, 10).map(renderCategories)}
                  </div>
                </div>
              )}

              {/* Trending Tags */}
              {search.trim().length === 0 && (
                <div>
                  {tagsData.length > 0 ? (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-5 h-5 rounded-md bg-[#DCFCE7] flex items-center justify-center">
                          <FontAwesomeIcon icon={faFire} className="text-[#F97316] w-2.5 h-2.5" />
                        </div>
                        <span className="text-[12px] font-black text-[#111827]">Trending</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {tagsData.map((item: string, i: number) => (
                          <Link
                            key={i}
                            href={`/search?query=${item}`}
                            onClick={() => setIsDropdownVisible(false)}
                            className="px-3 py-1.5 bg-[#F8FAFC] text-[#4B5563] border border-[#E5E7EB] text-[11px] font-semibold rounded-full hover:bg-[#1E3A5F] hover:text-white hover:border-[#1E3A5F] transition-all duration-200"
                          >
                            #{item}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
                      <FontAwesomeIcon icon={faMagnifyingGlass} className="text-[#E5E7EB] w-7 h-7" />
                      <p className="text-[12px] text-[#4B5563] font-medium">Type to search stores &amp; deals</p>
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Footer CTA */}
            {search.trim().length >= 3 && !loading && hasResults && (
              <div className="px-4 py-3 border-t border-[#F8FAFC] bg-[#F8FAFC]">
                <button
                  onClick={submitSearchNow}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-[12px] font-bold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md group"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="w-3 h-3" />
                  Search for &ldquo;{search.trim().length > 24 ? search.trim().slice(0, 24) + '…' : search.trim()}&rdquo;
                  <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
